import type { LucideIcon } from 'lucide-react';
import { ArrowLeft, Plus, Save, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/shared/components/ui';
import { cn } from '@/shared/utils';

interface DetailPageHeaderProps {
  /** Back link URL */
  backHref?: string;
  /** Back link label */
  backLabel?: string;
  /** Page title */
  title: string;
  /** Large icon for the header (shown in avatar box) */
  icon?: LucideIcon;
  /** Custom avatar content (overrides icon, e.g. for initials) */
  avatarContent?: ReactNode;
  /** Whether this is create mode (shows Plus icon if no avatarContent) */
  isCreateMode?: boolean;
  /** Meta information badges to display below title */
  metaItems?: Array<{
    icon?: LucideIcon;
    label: string;
    href?: string;
  }>;
  /** Subtitle text for create mode */
  subtitle?: string;
  /** Primary action button (e.g. Save) */
  primaryAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
  };
  /** Secondary action button (e.g. Discard) */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  /** Additional custom actions */
  actions?: ReactNode;
  className?: string;
}

/**
 * Detail page header component matching the prototype design.
 * Includes back button, avatar/icon, title, meta info, and action buttons.
 */
export function DetailPageHeader({
  backHref,
  backLabel,
  title,
  icon: Icon,
  avatarContent,
  isCreateMode,
  metaItems,
  subtitle,
  primaryAction,
  secondaryAction,
  actions,
  className,
}: DetailPageHeaderProps) {
  const shouldRenderBackLink = Boolean(backHref && backLabel);

  return (
    <div className={cn('mb-8', className)}>
      {/* Back Button */}
      {shouldRenderBackLink && (
        <Link
          to={backHref!}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-4 transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          {backLabel}
        </Link>
      )}

      <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
        {/* Left side: Avatar + Title */}
        <div className="flex items-start gap-5">
          {/* Avatar / Icon Box */}
          <div className="w-20 h-20 bg-secondary rounded-2xl flex items-center justify-center text-primary border border-border shadow-sm shrink-0">
            {avatarContent ? (
              avatarContent
            ) : isCreateMode ? (
              <Plus size={32} />
            ) : Icon ? (
              <Icon size={32} />
            ) : (
              <Plus size={32} />
            )}
          </div>

          <div className="pt-1">
            <h1 className="text-3xl font-bold text-foreground mb-2">{title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              {metaItems?.map((item, index) => (
                <span
                  key={index}
                  className={cn(
                    'flex items-center gap-1.5',
                    index === 0 && 'bg-muted/50 px-2 py-1 rounded-md border border-border'
                  )}
                >
                  {item.icon && <item.icon size={14} />}
                  {item.href ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-primary hover:underline"
                    >
                      {item.label}
                    </a>
                  ) : (
                    item.label
                  )}
                </span>
              ))}
              {subtitle && <span>{subtitle}</span>}
            </div>
          </div>
        </div>

        {/* Right side: Actions */}
        <div className="flex items-center gap-3">
          {actions}
          {secondaryAction && (
            <Button variant="outline" onClick={secondaryAction.onClick}>
              <X size={16} className="mr-2" />
              {secondaryAction.label}
            </Button>
          )}
          {primaryAction && (
            <Button
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled || primaryAction.loading}
            >
              <Save size={16} className="mr-2" />
              {primaryAction.label}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
